import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { COMPANY, ASSETS, scrollToId, scrollTop } from "@/lib/site";
import { AnimatedLogo } from "@/components/site/AnimatedLogo";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#manifesto" },
  { label: "Drive With Us", href: "#careers" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-ink/80 backdrop-blur-xl border-white/10" : "border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
        <button
          onClick={() => scrollTop()}
          data-testid="nav-logo"
          className="flex items-center text-left"
        >
          <AnimatedLogo src={ASSETS.logo} className="h-12 md:h-14" />
        </button>

        <div className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm uppercase tracking-widest text-[#a1a1a8] transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#contact")}
            data-testid="nav-quote-btn"
            className="group flex items-center gap-2 bg-brand px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-ink transition-colors hover:bg-white"
          >
            Get a Quote
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          className="md:hidden"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 bg-ink md:hidden"
          >
            <div className="flex flex-col px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="border-b border-white/5 py-4 text-left text-sm uppercase tracking-widest text-[#a1a1a8]"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-4 bg-brand px-5 py-3 text-sm font-semibold uppercase tracking-wide text-ink"
              >
                Get a Quote
              </button>
              <a href={COMPANY.phoneHref} className="mt-3 text-center text-sm text-[#a1a1a8]">
                {COMPANY.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
